import {
  Badge,
  Button,
  IconButton,
  ViewPlaceholder,
} from '@vrplatform/components';
import { useMutation } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { Drawer } from '@vrplatform/components-legacy';
import { type Maybe, utc } from '@vrplatform/utils';
import {
  Avatar,
  Box,
  Center,
  Group,
  LoadingOverlay,
  Stack,
  Title,
  Tooltip,
  rem,
  useMantineColorScheme,
} from '@mantine/core';
import { Text } from '@mantine/core';
import {
  type Address,
  formatAddress,
  formatPercentage,
  getCountryByIsoCode,
  getStateByIsoCode,
} from '@vrplatform/ui-common';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  DrawerCollapsableTable,
  DrawerHeader,
} from '../../../drawers/_components';
import { StatementStatusBadge } from '../../statements/_components';
import { ListingInfoCard } from '../_components';
import { ListingEllipsisMenuItems } from '../_components/ListingEllipsisMenuItems';
import { ListingEllipsisMenuModals } from '../_components/ListingEllipsisMenuModals';
import { ListingDeactivationBanner } from '../detail/_components';
import { ListingEditForm } from './ListingEditForm';
import { ListingPeriodForm } from './ListingPeriodForm';
import { useListingDetailDrawer } from './useListingDetailDrawer';
import {
  type Listing,
  useListingDetailDrawerQuery,
} from './useListingDetailDrawerQuery';
import { useOwnerDetailDrawer } from '../../../drawers';

export const LedgerListingDetailDrawer = () => {
  const { opened, close, listingId, view, setView } = useListingDetailDrawer();

  const goto = useNavigate();

  const { isLoading, data } = useListingDetailDrawerQuery(listingId);
  const listing = data?.listing;

  const [modalOpened, setModalOpened] = useState<
    'deactivate' | 'opening-balance' | null
  >(null);

  const closeModal = () => setModalOpened(null);

  const { mutate: updateListing } = useMutation(
    (
      q,
      args: {
        listingId: string;
        tenantId: string;
        inputs: {
          title: string;
          address: Address & { country: string; state: string } & {
            id: Maybe<string>;
          };
        };
      }
    ) => {
      const line = args.inputs.address.line1.trim();
      const line2 = args.inputs.address.line2?.trim() || '';
      const city = args.inputs.address.city.trim();
      const state = args.inputs.address.state?.trim();
      const postalCode = args.inputs.address.postcode.trim();
      const country = args.inputs.address.country.trim();

      return q.insertListing({
        object: {
          id: args.listingId,
          title: args.inputs.title.trim(),
          tenantId: args.tenantId,
          addressRelation: {
            data: {
              id: args.inputs.address.id || undefined,
              line,
              lineDetails: line2,
              city,
              state,
              postalCode,
              country,
              stateCode: args.inputs.address.stateCode,
              countryCode: args.inputs.address.countryCode,
              full: formatAddress({
                line1: line,
                line2,
                city,
                state,
                postcode: postalCode,
                country,
                stateCode: args.inputs.address.stateCode,
                countryCode: args.inputs.address.countryCode,
              }),
              tenant_id: args.tenantId,
            },
            on_conflict: {
              constraint: 'address_pkey',
              update_columns: [
                'line',
                'lineDetails',
                'city',
                'state',
                'postalCode',
                'country',
                'full',
              ],
            },
          },
        },
        on_conflict: {
          constraint: 'listing_pkey',
          update_columns: ['title', 'address_id'],
        },
      })?.id;
    },
    {
      invalidateQueryKeys: ['listings'],
    }
  );

  const showMenuItems = view === 'overview' && listing;

  const isDeletedInPms = listing?.sourceStatus === 'deleted';

  const resetView = useCallback(() => {
    const path = window.location.pathname;

    if (path.startsWith('/listing/')) {
      close();
    } else {
      setView('overview');
    }
  }, [close, setView]);

  const handlers = useMemo(
    () => ({
      // openDeleteModal: () => setModalOpened('delete'),
      // openMigrationModal: () => setModalOpened('migrate'),
      openDeactivationModal: () => setModalOpened('deactivate'),
      openOpeningBalanceModal: () => setModalOpened('opening-balance'),
    }),
    []
  );

  return (
    <Drawer opened={opened} onClose={close} size={550}>
      <DrawerHeader
        closeDrawer={close}
        title={
          view === 'record-ownership' ? (
            'Add new ownership period'
          ) : (
            <Group wrap="nowrap" gap={rem(8)}>
              {isDeletedInPms && (
                <Tooltip label="This listing was deleted in your PMS">
                  <Icon
                    icon="AlertTriangleIcon"
                    color={(theme) => theme.colors.orange[7]}
                    size={20}
                  />
                </Tooltip>
              )}
              <Title order={3} display="block">
                {listing?.name || 'Missing name'}
              </Title>
            </Group>
          )
        }
        type="Listing"
        loading={isLoading}
        menuItems={
          showMenuItems && (
            <ListingEllipsisMenuItems listing={listing} handlers={handlers} />
          )
        }
      >
        {showMenuItems && (
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              goto(`/listing/${listing.id}`);
            }}
            variant="outline"
            icon="ArrowRightCircleIcon"
            tooltip="Go to detail"
          />
        )}
      </DrawerHeader>
      {!listing && !isLoading ? (
        <ViewPlaceholder
          title="No listing found"
          description="Has the listing been deleted?"
          icon="CommentQuestionIcon"
          backgroundColor="transparent"
        />
      ) : view === 'edit' && listing ? (
        <ListingEditForm
          initialValues={{
            address: listing.address.values || '',
            title: listing.title || '',
          }}
          listingName={listing.pmsName || ''}
          isPmsConnection={listing.isPmsListing}
          onReset={resetView}
          isLoading={isLoading}
          handleSubmit={async (values) => {
            await updateListing({
              args: {
                inputs: {
                  title: values.title,
                  address: {
                    id: listing.addressId,
                    line1: values.address.line1,
                    line2: values.address.line2,
                    city: values.address.city,
                    postcode: values.address.postcode,
                    countryCode: values.address.countryCode,
                    stateCode: values.address.stateCode || '',
                    country:
                      getCountryByIsoCode(values.address.countryCode)?.name ||
                      '',
                    state:
                      getStateByIsoCode(
                        values.address.stateCode,
                        values.address.countryCode
                      )?.name || '',
                  },
                },
                listingId: listing.id,
                tenantId: listing.tenantId,
              },
            }).then(() => resetView());
          }}
        />
      ) : view === 'record-ownership' && listing ? (
        <ListingPeriodForm
          initialValues={undefined}
          onReset={() => setView('overview')}
          loadingQuery={isLoading}
          previousValues={listing.currentPeriod}
          listingId={listing.id}
        />
      ) : (
        <ListingDetail
          listing={listing}
          isLoading={isLoading}
          openDeactivationModal={handlers.openDeactivationModal}
        />
      )}
      <ListingEllipsisMenuModals
        listing={listing || null}
        // deleteModal={{
        //   opened: modalOpened === 'delete',
        //   closeModal,
        // }}
        deactivationModal={{
          opened: modalOpened === 'deactivate',
          closeModal,
        }}
        openingBalanceModal={{
          opened: modalOpened === 'opening-balance',
          closeModal,
        }}
      />
    </Drawer>
  );
};

const ListingDetail = ({
  listing,
  isLoading,
  openDeactivationModal,
}: {
  listing: Maybe<Listing>;
  isLoading: boolean;
  openDeactivationModal: () => void;
}) => {
  if (!listing) return null;

  return (
    <Stack
      gap={'md'}
      mb="md"
      sx={{
        position: 'relative',
      }}
    >
      <ListingDeactivationBanner
        pmsStatus={listing.pmsStatus}
        sourceStatus={listing.sourceStatus}
        openListingDeactivationModal={openDeactivationModal}
        {...listing.deactivation}
      />

      <ListingInfoCard
        {...listing}
        formattedAddress={listing.address.full}
        currentBusinessModel={listing.currentPeriod?.businessModel}
      />

      <ListingPeriod period={listing.currentPeriod} />

      <Connections
        listingConnections={listing.listingConnections}
        listingId={listing.id}
      />

      <Statements statements={listing.ownerStatements} listingId={listing.id} />

      <LoadingOverlay
        visible={isLoading}
        loaderProps={{
          size: 'sm',
        }}
      />
    </Stack>
  );
};

const ListingPeriod = ({ period }: { period: Listing['currentPeriod'] }) => {
  const { open: openOwnerDetailDrawer } = useOwnerDetailDrawer();
  const { colorScheme } = useMantineColorScheme();
  const { setView } = useListingDetailDrawer();

  const rows = period?.members || [];

  return (
    <DrawerCollapsableTable
      title="Current Ownership"
      defaultOpened
      rightSection={
        <Button
          variant="light"
          leftIcon={'PlusIcon'}
          onClick={() => setView('record-ownership')}
        >
          Record {rows.length ? 'change' : 'ownership'}
        </Button>
      }
      rowData={rows}
      columns={[
        {
          header: 'Owner',
          accessorKey: 'id',
          Cell: ({ row }) => {
            const member = row.original;

            return (
              <Badge
                color={member.ownerType === 'company' ? 'violet' : undefined}
                leftIcon={
                  <Icon
                    icon={
                      member.ownerType === 'company' ? 'OfficeIcon' : 'UserIcon'
                    }
                    size={14}
                    style={{ marginRight: 5 }}
                    color={(theme) =>
                      theme.colors[
                        member.ownerType === 'company'
                          ? 'violet'
                          : theme.primaryColor
                      ][5]
                    }
                  />
                }
                sx={{
                  color: colorScheme === 'dark' ? undefined : 'black',
                }}
              >
                {member.ownerName}
              </Badge>
            );
          },
        },
        {
          header: 'Split',
          accessorKey: 'split',
          maxSize: 150,
          mantineTableBodyCellProps: {
            align: 'right',
          },
          Cell: ({ row }) => {
            const data = row.original;
            return formatPercentage(
              data.ownerSplitSuperBasePoints || 0,
              'super-base-points'
            );
          },
        },
      ]}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" color="gray">
            No current period
          </Text>
        </Center>
      )}
      onRowClick={{
        handler: (row) => openOwnerDetailDrawer(row.original.ownerId),
      }}
    />
  );
};

const Statements = ({
  statements,
  listingId,
}: {
  statements: Listing['ownerStatements'];
  listingId: string;
}) => {
  const goto = useNavigate();

  return (
    <DrawerCollapsableTable
      title="Published Owner Statements"
      rightSection={
        <Button
          variant="light"
          rightIcon={'ArrowRightIcon'}
          onClick={() => goto(`/listing/${listingId}/owner-statements`)}
        >
          View all
        </Button>
      }
      defaultOpened
      paginationSize={5}
      rowData={statements}
      columns={[
        {
          header: 'Owner',
          accessorKey: 'id',
          Cell: ({ row }) => {
            const statement = row.original;

            return (
              <Box>
                <Text component="span" display="block" size="sm">
                  {utc(statement.startAt).format('MMMM YYYY')}
                </Text>
                {!!statement.owners?.length && (
                  <Text component="span" display="block" size="xs" color="gray">
                    {statement.owners.map((owner) => owner.name).join(', ')}
                  </Text>
                )}
              </Box>
            );
          },
        },
        {
          header: 'Status',
          accessorKey: 'status',
          maxSize: 150,
          mantineTableBodyCellProps: {
            align: 'right',
          },
          Cell: ({ row }) => {
            const statement = row.original;

            return <StatementStatusBadge status={statement.status} />;
          },
        },
      ]}
      onRowClick={{
        disabled: (row) => !row.original.periodId,
        handler: (row) =>
          goto(
            `/statement/${row.original.periodId}?date=${utc(
              row.original.startAt
            ).yyyymmdd()}`
          ),
      }}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" color="gray">
            No owner statements
          </Text>
        </Center>
      )}
    />
  );
};

const Connections = ({
  listingConnections,
}: {
  listingConnections: Listing['listingConnections'];
  listingId: string;
}) => {
  return (
    <DrawerCollapsableTable
      title="Connections"
      rightSection={null}
      defaultOpened
      rowData={listingConnections}
      columns={[
        {
          header: 'Owner',
          accessorKey: 'id',
          Cell: ({ row }) => {
            const listingConnection = row.original;

            return (
              <Group wrap="nowrap" gap="sm">
                <Avatar
                  src={listingConnection.icon}
                  size="sm"
                  styles={{
                    placeholder: { visibility: 'hidden' },
                  }}
                  sx={(theme) => ({
                    border: `1px solid ${theme.colors.gray[2]}`,
                  })}
                />
                <Box>
                  <Text size="sm" component="p" m={0}>
                    {listingConnection.name}
                  </Text>
                  <Text size="xs" component="p" color="gray" m={0}>
                    {listingConnection.app}
                  </Text>
                </Box>
              </Group>
            );
          },
        },
      ]}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" color="gray">
            No connections
          </Text>
        </Center>
      )}
    />
  );
};
